import { useMutation } from "@apollo/client";
import { DotsHorizontalIcon } from "@heroicons/react/outline";
import {
  ControlledMenu,
  Menu,
  MenuDivider,
  MenuItem,
  SubMenu,
} from "@szhsin/react-menu";
import moment from "moment";
import { ReactNode, useCallback, useState } from "react";
import {
  CancelMeetingMutationMutation,
  CancelMeetingMutationMutationVariables,
  DeleteDraftMeetingNewPageMutationMutation,
  DeleteDraftMeetingNewPageMutationMutationVariables,
  MeetingDialogFragmentFragment,
  MeetingEditTypeEnum,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  OnlineVideoConference,
  PermissionNode,
  SubmitOneononeNewPageMutation,
  SubmitOneononeNewPageMutationVariables,
  UpdateMeetingFinalizationStatusNewPageMutationMutation,
  UpdateMeetingFinalizationStatusNewPageMutationMutationVariables,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import cancelMeetingMutation from "@apps/meeting-dialog/graphql/cancel-meeting-mutation";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import { meetingDialogAction } from "@apps/meeting-dialog/meeting-dialog-constants";
import submitOneononeMutation from "@apps/meeting-new/components/prep/graphql/submit-oneonone-mutation";
import updateMeetingFinalizationStatusMutation from "@apps/meeting-new/components/prep/graphql/update-meeting-finalization-status-mutation";
import MeetingTypeDropdownOptions from "@apps/meeting-new/components/prep/meeting-type-dropdown-options";
import deleteDraftMeetingMutation from "@apps/meeting-new/graphql/delete-draft-meeting-mutation";
import getMeetingQuery from "@apps/meeting-new/graphql/get-meeting-query";
import SetAgendaAsTemplateDialog from "@apps/set-agenda-as-template-dialog/set-agenda-as-template-dialog";
import Templates from "@apps/templates/dialog";
import { currentUserVar, successNotificationVar } from "@cache/cache";
import Button, { ButtonProps } from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import { useLink } from "@components/link/link-helpers";
import { assertEdgesNonNull, assertNonNull, getUrl } from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import CalendarEventMenu from "./components/calendar-event-menu";
import CopyUrlMenu from "./components/copy-url-menu";
import CreateTemplateDialog from "./components/create-template-dialog";
import IgnoreMenu from "./components/ignore-menu";
import MeetingDetailsModal from "./components/meeting-details-modal";
import MeetingTranscriptionSubmenu from "./components/transcription-submenu";
import VideoConferenceMenu from "./components/video-conference-menu";

const MeetingDropdownMenu = ({
  meeting,
  meetingGroup,
  isContextMenu = false,
  buttonClassName = "",
  buttonProps = {},
  anchorPoint,
  onToggleMenu,
  onMenuChange,
  onOpenSendNotesDialog,
  onMeetingUpdated,
  children,
  ...menuProps
}: {
  buttonProps?: ButtonProps;
  meeting: {
    id: number;
    title?: string | null;
    organizer?: BasicUser | null;
    isRecurring: boolean;
    isFinalized?: boolean;
    canFinalize?: Pick<PermissionNode, "permission"> | null;
    hasIncompleteMandatoryTopics?: boolean;
    externalUrl?: string | null;
    topics?: MeetingViewMeetingNodeNewPageFragmentFragment["topics"];
    videoConferenceUrl?: string | null;
    draft: boolean;
    videoConferenceType?: OnlineVideoConference | null;
    transcript?: {
      id: number;
    } | null;
    canDelete?: Pick<PermissionNode, "permission"> | null;
    canUpdate?: Pick<PermissionNode, "permission"> | null;
    ignored: boolean;
    hasBotInMeeting: boolean;
    botIsRecording: boolean;
    botIsWaitingToJoinMeeting: boolean;
  };
  meetingGroup: {
    id: number;
    isFormalOneonone: boolean;
    facilitator?: BasicUser | null;
    hasTemplate?: boolean | null;
    addTranscriptionBotToMeetingsByDefault: boolean;
    transcribeMeetingsByDefault?: boolean | null;
  };
  children?: ReactNode;
  size?: number | string;
  className?: string;
  buttonClassName?: string;
  isContextMenu?: boolean;
  anchorPoint?: any;
  onToggleMenu?: (opened: boolean) => void;
  onMenuChange?: ({ open }: { open: boolean }) => void;
  onOpenSendNotesDialog?: () => void;
  onMeetingUpdated?: (data: { meeting: MeetingDialogFragmentFragment }) => void;
}) => {
  const [viewMeetingDetailsModal, setViewMeetingDetailsModal] = useState(false);
  const currentUser = currentUserVar();
  const {
    ConfirmationDialog: ConfirmationDialogThisMeeting,
    confirm: confirmThisMeeting,
  } = useConfirm(
    "Are you sure?",
    "Are you sure you want to delete this meeting?",
  );
  const {
    ConfirmationDialog: ConfirmationDialogThisAndFutureMeetings,
    confirm: confirmThisAndFutureMeetings,
  } = useConfirm(
    "Are you sure?",
    "Are you sure you want to delete this and all future meetings?",
  );
  const {
    ConfirmationDialog: ConfirmationDialogAllMeetings,
    confirm: confirmAllMeetings,
  } = useConfirm(
    "Are you sure?",
    "Are you sure you want to delete this and all future meetings?",
  );
  const link = useLink();

  const [openCreateTemplateDialog, setOpenCreateTemplateDialog] =
    useState(false);
  const [openTemplateDialog, setOpenTemplatesDialog] = useState(false);
  const [openSetAgendaAsTemplateDialog, setOpenSetAgendaAsTemplateDialog] =
    useState(false);
  const [isShowingMeetingDialogAction, setIsShowingMeetingDialogAction] =
    useState<meetingDialogAction | false>(false);
  const [cancelMeeting] = useMutation<
    CancelMeetingMutationMutation,
    CancelMeetingMutationMutationVariables
  >(cancelMeetingMutation);
  const [deleteDraftMeeting] = useMutation<
    DeleteDraftMeetingNewPageMutationMutation,
    DeleteDraftMeetingNewPageMutationMutationVariables
  >(deleteDraftMeetingMutation);

  const handleOpenTemplatesDialog = () => {
    setOpenTemplatesDialog(true);
  };
  const handleCreateTemplateDialog = () => {
    setOpenCreateTemplateDialog(true);
  };

  const handleSetAgendaAsTemplate = () => {
    setOpenSetAgendaAsTemplateDialog(true);
  };

  const handleClickDeleteDraftMeeting = useCallback(() => {
    deleteDraftMeeting({
      variables: {
        meetingId: meeting.id,
      },
      update: (cache) => {
        const cacheId = cache.identify(meeting);
        cache.evict({ id: cacheId });
        cache.gc();
      },
      onCompleted: () => {
        successNotificationVar({ title: "Meeting discarded." });
        link.redirect("/");
      },
    });
  }, [deleteDraftMeeting, meeting, link]);

  const handleClickCancelMeeting = useCallback(
    (editType: MeetingEditTypeEnum) => {
      cancelMeeting({
        variables: {
          editType,
          meetingId: meeting.id,
        },
        update: (cache) => {
          const cacheId = cache.identify(meeting);
          cache.evict({ id: cacheId });
          cache.gc();
        },
        onCompleted: () => {
          successNotificationVar({ title: "Meetings cancelled." });
          link.redirect("/");
        },
      });
    },
    [cancelMeeting, meeting, link],
  );

  const handleClickCancelThisMeeting = useCallback(async () => {
    const confirmation = await confirmThisMeeting();
    if (confirmation) {
      handleClickCancelMeeting(MeetingEditTypeEnum.SingleEvent);
    }
  }, [confirmThisMeeting, handleClickCancelMeeting]);
  const handleClickCancelThisAndFutureMeetings = useCallback(async () => {
    const confirmation = await confirmThisAndFutureMeetings();
    if (confirmation) {
      handleClickCancelMeeting(MeetingEditTypeEnum.ThisEventAndFuture);
    }
  }, [confirmThisAndFutureMeetings, handleClickCancelMeeting]);
  const handleClickCancelAllMeetings = useCallback(async () => {
    const confirmation = await confirmAllMeetings();
    if (confirmation) {
      handleClickCancelMeeting(MeetingEditTypeEnum.AllEvents);
    }
  }, [confirmAllMeetings, handleClickCancelMeeting]);

  const meetingUrl = getUrl({
    meetingGroupId: meetingGroup.id,
    meetingId: meeting.id,
  });
  const handleClickEditMeetingSettings = () => {
    link.redirect(
      `${getUrl({
        meetingGroupId: meetingGroup.id,
      })}/settings?from=${meetingUrl}`,
    );
  };

  const topics = "topics" in meeting ? assertEdgesNonNull(meeting.topics) : [];
  const mandatoryTopics = topics.filter((topic) => topic.isMandatory);
  const mandatoryTopicsWithHiddenNotes = mandatoryTopics.filter((node) => {
    const hiddenIndividualNotes =
      node.individualNotes?.edges.filter(
        (edge) => edge?.node?.creator.id === currentUser.id && edge.node.hidden,
      ) || [];
    return hiddenIndividualNotes.length > 0;
  });
  const hasMandatoryTopicsWithHiddenNotes =
    mandatoryTopicsWithHiddenNotes.length > 0;
  const errorMatches = [
    {
      match: "Cannot finalize a meeting with incomplete mandatory topics",
      title: "In order to be finalized, all topics must contain notes.",
    },
  ];

  const [toggleFinalizeMeeting, { loading: loadingFinalize }] = useMutation<
    UpdateMeetingFinalizationStatusNewPageMutationMutation,
    UpdateMeetingFinalizationStatusNewPageMutationMutationVariables
  >(updateMeetingFinalizationStatusMutation);

  const handleSaveFinalizeMeeting = (newIsFinalized: boolean) => {
    const nonNullTopics =
      "topics" in meeting && meeting.topics && assertNonNull(meeting.topics);
    if (!nonNullTopics) {
      return;
    }
    toggleFinalizeMeeting({
      variables: {
        meetingId: meeting.id,
        isFinalized: newIsFinalized,
      },
      context: { errorMatches },
      optimisticResponse: {
        __typename: "Mutation",
        updateMeetingFinalizationStatus: {
          meeting: {
            ...meeting,
            __typename: "MeetingNode",
            isFinalized: newIsFinalized,
            finalizedAt: newIsFinalized ? moment().format() : null,
            finalizedBy: newIsFinalized ? currentUser : null,
            topics: {
              ...nonNullTopics,
              __typename: "TopicNodeConnection",
              edges: nonNullTopics.edges.map((edge) => {
                const topicEdge = assertNonNull(edge);
                const topicEdgeNode = assertNonNull(topicEdge.node);
                return {
                  ...topicEdge,
                  node: {
                    ...topicEdgeNode,
                    canUpdate: {
                      ...topicEdgeNode.canUpdate,
                      permission: !newIsFinalized,
                    },
                    canDelete: {
                      ...topicEdgeNode.canDelete,
                      permission: !newIsFinalized,
                    },
                  },
                };
              }),
            },
          },
          __typename: "UpdateMeetingFinalizationStatus",
        },
      },
    });
  };

  const handleClickFinalizeMeeting = () => handleSaveFinalizeMeeting(true);

  const [submitOneonone, { loading: loadingSubmit }] = useMutation<
    SubmitOneononeNewPageMutation,
    SubmitOneononeNewPageMutationVariables
  >(submitOneononeMutation);

  const handleClickSubmit = () => {
    submitOneonone({
      variables: {
        meetingId: meeting.id,
        unhideIndividualNotes: true,
      },
    });
  };

  const organizerId = meeting.organizer?.id;

  const isCurrentUserTheOrganizer = organizerId === currentUser.id;

  const items = (
    <>
      {children}
      <>
        <MenuItem
          className={"text-sm"}
          onClick={() => setViewMeetingDetailsModal(true)}
        >
          <div>View Meeting Details</div>
        </MenuItem>
        <MenuDivider />
        {meeting.draft && meeting.canUpdate?.permission && (
          <>
            <MenuItem
              className={"text-sm"}
              onClick={() =>
                setIsShowingMeetingDialogAction(meetingDialogAction.publish)
              }
            >
              Publish Meeting
            </MenuItem>
            <MenuItem
              className={"text-sm"}
              onClick={handleClickDeleteDraftMeeting}
            >
              <div>Discard Draft Meeting</div>
            </MenuItem>
            <MenuDivider />
          </>
        )}

        {isCurrentUserTheOrganizer && (
          <MenuItem
            className={"text-sm"}
            onClick={() =>
              setIsShowingMeetingDialogAction(meetingDialogAction.edit)
            }
            disabled={!isCurrentUserTheOrganizer}
          >
            Edit Meeting
          </MenuItem>
        )}
        {!meeting.draft && meeting.canDelete?.permission && (
          <>
            {meeting.isRecurring ? (
              <SubMenu label="Delete meeting" className={"text-sm"}>
                <MenuItem
                  className={"text-sm"}
                  onClick={handleClickCancelThisMeeting}
                >
                  <div>This Meeting Only</div>
                </MenuItem>
                <MenuItem
                  className={"text-sm"}
                  onClick={handleClickCancelThisAndFutureMeetings}
                >
                  <div>This and All Future Meetings</div>
                </MenuItem>
                <MenuItem
                  className={"text-sm"}
                  onClick={handleClickCancelAllMeetings}
                >
                  <div>All Meetings</div>
                </MenuItem>
              </SubMenu>
            ) : (
              <MenuItem
                className={"text-sm"}
                onClick={handleClickCancelThisMeeting}
              >
                <div>Delete Meeting</div>
              </MenuItem>
            )}
          </>
        )}
      </>

      <MenuDivider />
      <MeetingTypeDropdownOptions
        meetingGroup={meetingGroup}
        meeting={meeting}
      />

      <ConfirmationDialogAllMeetings />
      <ConfirmationDialogThisAndFutureMeetings />
      <ConfirmationDialogThisMeeting />
      {!isContextMenu && meeting && (
        <>
          <SubMenu className="text-sm" label="Templates">
            <MenuItem
              className="text-sm"
              onClick={handleClickEditMeetingSettings}
            >
              Edit Meeting Template
            </MenuItem>
            <MenuItem className="text-sm" onClick={handleOpenTemplatesDialog}>
              Apply a Template...
            </MenuItem>
            <MenuItem
              className="text-sm block"
              onClick={handleCreateTemplateDialog}
            >
              Save Agenda to Template Library
            </MenuItem>
            {meeting.isRecurring && (
              <MenuItem
                className="text-sm block"
                onClick={handleSetAgendaAsTemplate}
              >
                Reuse this Agenda in Upcoming Meetings
              </MenuItem>
            )}
            <MenuDivider />
            <MenuItem
              className="text-sm"
              onClick={() => link.redirect("/templates")}
            >
              View All Templates
            </MenuItem>
          </SubMenu>
          <MenuDivider />
        </>
      )}

      {meeting.externalUrl && (
        <CalendarEventMenu externalUrl={meeting.externalUrl} />
      )}
      {meeting.videoConferenceUrl && meeting.videoConferenceType && (
        <VideoConferenceMenu
          videoConferenceUrl={meeting.videoConferenceUrl}
          videoConferenceType={meeting.videoConferenceType}
        />
      )}
      {(meeting.externalUrl || meeting.videoConferenceUrl) && <MenuDivider />}

      {!isContextMenu && meeting && (
        <>
          <CopyUrlMenu meeting={meeting} meetingGroup={meetingGroup} />
          <MenuItem className={"text-sm"} onClick={onOpenSendNotesDialog}>
            Share Notes
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {meetingGroup.isFormalOneonone &&
      !meeting.isFinalized &&
      meeting.hasIncompleteMandatoryTopics &&
      meeting.canFinalize?.permission ? (
        <>
          <SubMenu label="Finalize Meeting" className={"text-sm"}>
            <MenuItem className={"text-sm"} disabled>
              <div className="flex flex-col">
                <div className="text-gray-700 font-semibold">
                  Mandatory Topics are Incomplete
                </div>
                <div className="text-gray-500 max-w-64 gap-4">
                  You must add notes to all the topics with a{" "}
                  <span className="text-xs bg-amber-100 text-amber-700 px-1 py-0.5">
                    Mandatory
                  </span>{" "}
                  tag, and make sure that they are not set to "Make private".
                </div>
              </div>
            </MenuItem>
            <MenuDivider />
            <MenuItem
              className={"text-sm"}
              onClick={handleClickSubmit}
              disabled={loadingSubmit}
            >
              <div>Reveal my notes to all participants</div>
            </MenuItem>
          </SubMenu>
          <MenuDivider />
        </>
      ) : meetingGroup.isFormalOneonone &&
        !meeting.isFinalized &&
        meeting.canFinalize?.permission ? (
        <>
          <MenuItem
            disabled={loadingFinalize || hasMandatoryTopicsWithHiddenNotes}
            className={"text-sm flex flex-col gap-1 items-start justify-start"}
            onClick={handleClickFinalizeMeeting}
          >
            <div className="text-left">Finalize</div>
            {hasMandatoryTopicsWithHiddenNotes && (
              <div className="text-xs text-gray-500">
                You must reveal all your notes in order to finalize
              </div>
            )}
          </MenuItem>
          <MenuDivider />
        </>
      ) : null}

      {meeting.canUpdate?.permission && (
        <>
          <MenuItem
            className={"text-sm"}
            onClick={handleClickEditMeetingSettings}
          >
            <div>Slack Notifications</div>
          </MenuItem>
          <MenuDivider />
        </>
      )}

      {!meeting.draft && <MeetingTranscriptionSubmenu meeting={meeting} />}

      <SubMenu label="More..." className="text-sm">
        <IgnoreMenu meeting={meeting} meetingGroup={meetingGroup} />
        <MenuDivider />
        <MenuItem className={"text-sm"}>
          <a
            href={`${getUrl({
              meetingGroupId: meetingGroup.id,
              meetingId: meeting.id,
            })}.md`}
            target="_blank"
            rel="noreferrer"
          >
            Markdown
          </a>
        </MenuItem>
      </SubMenu>
    </>
  );

  // Render
  return (
    <>
      {meeting && viewMeetingDetailsModal && (
        <MeetingDetailsModal
          meetingId={meeting.id}
          onClose={() => {
            setViewMeetingDetailsModal(false);
          }}
        />
      )}
      {meeting && meetingGroup && openTemplateDialog && (
        <Templates
          meeting={meeting}
          meetingGroup={meetingGroup}
          onClose={() => setOpenTemplatesDialog(false)}
        />
      )}
      {openCreateTemplateDialog && (
        <CreateTemplateDialog
          meeting={meeting}
          onClose={() => setOpenCreateTemplateDialog(false)}
        />
      )}
      {openSetAgendaAsTemplateDialog && (
        <SetAgendaAsTemplateDialog
          meeting={meeting}
          meetingGroup={meetingGroup}
          onClose={() => setOpenSetAgendaAsTemplateDialog(false)}
        />
      )}

      {isShowingMeetingDialogAction && (
        <MeetingDialog
          meetingId={meeting.id}
          meetingGroupId={meetingGroup.id}
          onClose={() => setIsShowingMeetingDialogAction(false)}
          defaultAction={isShowingMeetingDialogAction}
          onSaved={(data) => {
            if (onMeetingUpdated) {
              onMeetingUpdated(data);
            }
            setIsShowingMeetingDialogAction(false);
          }}
          refetchQueries={[getMeetingQuery]}
        />
      )}
      {isContextMenu && onToggleMenu ? (
        <ControlledMenu
          {...menuProps}
          portal={true}
          className="z-dropdown"
          anchorPoint={anchorPoint}
          onClose={() => onToggleMenu(false)}
        >
          {items}
        </ControlledMenu>
      ) : (
        <Menu
          onMenuChange={onMenuChange}
          className="z-dropdown"
          portal
          menuButton={
            <Button
              theme={buttonTheme.iconGray}
              className={buttonClassName}
              icon={DotsHorizontalIcon}
              aria-label="Meeting dropdown button"
              {...buttonProps}
            />
          }
          aria-label="Meeting dropdown list"
        >
          {items}
        </Menu>
      )}
    </>
  );
};

export default MeetingDropdownMenu;
